import type { ViewResolution } from '@ayumia/contracts';
import type { ApiClient } from '../../api/client';
import { modelChangeApproval } from '../editor/model-change-approval';
import { text } from '../../app/i18n';
import type { Finding, GovernancePort, ImpactReport, Proposal } from '../editor/editor-types';
import type { CatalogueField, View } from './views-model';

/**
 * Branchement du profil « vue » sur l'API v1 : les lectures du Studio des vues (`ListCustomizableViews`,
 * `GetViewStudio`, `ValidateViewDraft`, `GetViewChangeImpact`, côté serveur dans
 * `packages/config-runtime/src/view-studio.ts`), `GetView` pour le résumé de l'objet et les composants de
 * l'aperçu, et le circuit `ModelChange` étendu aux vues (`model-change` 1.1.0, `target_kind: view`).
 * Le navigateur ne nomme jamais le tenant ni l'auteur : la session les porte.
 */

export interface CustomizableView {
  artifact_id: string;
  artifact_version: string;
  origin: string;
  title?: Record<string, string>;
  target_object_type: string;
  overridden_by?: { artifact_id: string; artifact_version: string; base_version: string };
  parent_moved?: boolean;
  editable_in_place: boolean;
}

export interface ViewStudioDetail {
  target: { artifact_id: string; artifact_version: string; origin: string };
  edited: { artifact_id: string; artifact_version: string | null; exists: boolean; override: boolean };
  /** Corps de départ : la surcharge active, sinon la vue visée. */
  body: View;
  /** Vue du Core ou du Pack, quand on édite sa surcharge. */
  parent_body?: View;
  base_version?: string;
  parent_moved?: boolean;
  object_type: string;
  readers: Array<{ role: string; label?: Record<string, string> }>;
  fields: CatalogueField[];
}

export interface OpenedView {
  detail: ViewStudioDetail;
  /** Résolution de la vue servie (objet, composants) : l'aperçu en a besoin, sans commande. */
  resolution: ViewResolution;
}

interface ImpactData {
  artifact_id: string;
  edited_artifact_id: string;
  base_artifact_version: string;
  impact: {
    diff: Array<{ path: string; summary: string }>;
    usages: Array<{ artifact_id: string; kind: string; artifact_version: string }>;
    records: number | null;
    override: { artifact_id: string; creates: boolean; base_version?: string; parent_version?: string; parent_moved?: boolean };
  };
}

interface ModelChangeRow {
  id: string;
  reference?: string;
  status: 'proposed' | 'published' | 'rejected';
  structural: boolean;
  reason: string;
  proposed_by?: string;
  decision_note?: string;
  published_artifact_version?: string;
}

export const ORIGIN_LABELS: Readonly<Record<string, string>> = { core: 'Core', pack: 'Pack', customer: 'Client', solution: 'Solution' };
const KIND_LABELS: Readonly<Record<string, string>> = { navigation: 'Navigation', view: 'Vue', dashboard: 'Tableau de bord', workflow_definition: 'Workflow' };

export class ViewsStudioApi {
  constructor(private readonly client: ApiClient) {}

  async list(): Promise<CustomizableView[]> {
    return (await this.client.query<CustomizableView[]>('ListCustomizableViews')).data;
  }

  async open(artifactId: string): Promise<OpenedView> {
    const detail = (await this.client.query<ViewStudioDetail>('GetViewStudio', { artifact_id: artifactId })).data;
    const resolution = (await this.client.query<ViewResolution>('GetView', { view_ref: artifactId })).data;
    return { detail, resolution };
  }

  async validate(artifactId: string, body: View): Promise<{ valid: boolean; findings: Finding[] }> {
    return (await this.client.queryPost<{ valid: boolean; findings: Finding[] }>('ValidateViewDraft', { artifact_id: artifactId, body: JSON.stringify(body) })).data;
  }

  async impact(artifactId: string, body: View): Promise<ImpactData> {
    return (await this.client.queryPost<ImpactData>('GetViewChangeImpact', { artifact_id: artifactId, body: JSON.stringify(body) })).data;
  }

  /** Port de gouvernance du socle pour une vue : la cible est la vue ouverte (Core ou Pack : surcharge du client). */
  governance(detail: ViewStudioDetail, me: string | undefined): GovernancePort<View> {
    const toProposal = (row: ModelChangeRow): Proposal => ({
      id: row.id,
      ...(row.reference === undefined ? {} : { reference: row.reference }),
      status: row.status,
      structural: row.structural,
      proposedByMe: me !== undefined && row.proposed_by === me,
      reason: row.reason,
      ...(row.decision_note === undefined ? {} : { decisionNote: row.decision_note }),
      ...(row.published_artifact_version === undefined ? {} : { publishedVersion: row.published_artifact_version }),
    });
    const reload = async (id: string): Promise<Proposal> => toProposal((await this.client.getObject<ModelChangeRow>('ModelChange', id)).data);
    const target = detail.target.artifact_id;
    return {
      wording: { minor: 'Présentation seulement', validation: 'Contrat de vue, périmètre de la liste, champs cités, lisibilité par les rôles, index.' },
      validate: (draft) => this.validate(target, draft),
      impact: async (draft) => viewImpactReport(await this.impact(target, draft)),
      pending: async () => {
        const rows = (await this.client.listObjects<ModelChangeRow>('ModelChange', { filter: { target_artifact_id: { eq: target } }, sort: [{ field: 'proposed_at', direction: 'desc' }], limit: 1 })).data;
        return rows[0] === undefined ? null : toProposal(rows[0]);
      },
      propose: async (draft, reason) => {
        const result = await this.client.command<{ result?: { model_change_id?: string } }>('ProposeModelChange', {
          payload: {
            target_kind: 'view',
            target_artifact_id: target,
            base_artifact_version: detail.edited.artifact_version ?? detail.target.artifact_version,
            object_type: detail.object_type,
            changes: [{ kind: 'replace_view_body', body: draft }],
            reason,
          },
        });
        const id = result.result?.model_change_id;
        if (id === undefined) throw new Error('La proposition n’a pas rendu son identifiant.');
        return reload(id);
      },
      approve: async (proposal, note) => {
        await this.client.command('ApproveModelChange', { payload: note === undefined ? {} : { note }, target: { type: 'ModelChange', id: proposal.id } });
        return reload(proposal.id);
      },
      canApprove: (proposal) => modelChangeApproval(this.client, proposal.id),
      reject: async (proposal, reason) => {
        await this.client.command('RejectModelChange', { payload: { reason }, target: { type: 'ModelChange', id: proposal.id } });
        return reload(proposal.id);
      },
    };
  }
}

/** Rapport d'impact du socle, à partir de la réponse du serveur. */
export function viewImpactReport(data: ImpactData): ImpactReport {
  const { impact } = data;
  const override = impact.override;
  return {
    change: { structural: false, changes: impact.diff.map((entry) => ({ path: entry.path, kind: 'label' as const })) },
    sections: [
      { title: 'Ce qui change', empty: 'Aucune différence avec la vue active.', items: impact.diff.map((entry) => ({ label: entry.summary, detail: entry.path })) },
      { title: 'Écrans et menus qui ouvrent cette vue', empty: 'Aucun autre artefact ne la cite.', items: impact.usages.map((usage) => ({ label: usage.artifact_id, detail: `${KIND_LABELS[usage.kind] ?? usage.kind}, ${usage.artifact_version}` })) },
      {
        title: 'Couche client',
        empty: '',
        items: [
          { label: override.creates ? `Surcharge créée : ${override.artifact_id}` : `Nouvelle version de ${override.artifact_id}` },
          ...(override.parent_moved === true ? [{ label: `La vue d’origine est en ${override.parent_version} ; la surcharge part de ${override.base_version}`, detail: 'ses changements n’entrent pas d’eux-mêmes' }] : []),
        ],
      },
    ],
    summary: `${impact.records === null ? 'Les enregistrements' : `${impact.records} enregistrement(s)`} s’affiche(nt) avec cette présentation ; aucune donnée n’est modifiée, la lecture reste autorisée champ par champ.`,
  };
}

export const viewTitle = (item: { title?: Record<string, string> | undefined; artifact_id: string }): string => text(item.title, 'fr', item.artifact_id);
